import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MessageCircle, Phone, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE, buildWhatsAppLink } from "@/lib/site-config";

export default function EnquiryButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    standard: "",
    school: "",
  });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid =
    form.name.trim() &&
    form.phone.trim().replace(/\D/g, "").length >= 10 &&
    form.standard.trim();

  const buildMessage = () =>
    `Hello, I am interested in admission.\n\nName: ${form.name}\nPhone: ${form.phone}\nClass: ${form.standard}\nSchool: ${form.school || "-"}`;

  const handleWhatsApp = () => {
    if (!isValid) {
      toast.error("Please fill name, phone (10+ digits) and class.");
      return;
    }
    window.open(buildWhatsAppLink(buildMessage()), "_blank", "noopener");
    toast.success("Opening WhatsApp…");
  };

  const handleCall = () => {
    if (!isValid) {
      toast.error("Please fill name, phone (10+ digits) and class.");
      return;
    }
    window.location.href = `tel:${SITE.phoneTel}`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Desktop vertical right-edge trigger */}
      <DialogTrigger asChild>
        <button
          data-testid="enquiry-vertical-btn"
          className="group fixed right-0 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-2 rounded-l-md bg-red-600 px-2.5 py-5 text-white shadow-lg transition-all hover:bg-red-700 md:inline-flex"
          aria-label="Open enquiry form"
        >
          <ChevronRight className="h-4 w-4 -rotate-180 transition-transform group-hover:-translate-x-0.5" />
          <span className="vertical-text text-[12px] font-bold uppercase tracking-[0.22em]">
            Enquire Now
          </span>
        </button>
      </DialogTrigger>

      {/* Mobile floating bottom enquire pill */}
      <DialogTrigger asChild>
        <button
          data-testid="enquiry-mobile-btn"
          className="fixed bottom-5 left-1/2 z-30 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-red-600 px-5 py-3 text-sm font-bold text-white shadow-xl shadow-red-600/30 ring-2 ring-white transition-all hover:bg-red-700 md:hidden"
          aria-label="Open enquiry form"
        >
          <Send className="h-4 w-4" />
          Enquire Now
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="enquiry-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-slate-900">
            Quick Admission Enquiry
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Fill in your details and we&apos;ll connect with you over WhatsApp
            or call.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <Field
            label="Full Name"
            id="enq-name"
            placeholder="Student / Parent name"
            value={form.name}
            onChange={setField("name")}
            testid="enquiry-name"
          />
          <Field
            label="Phone Number"
            id="enq-phone"
            type="tel"
            placeholder="10-digit mobile"
            value={form.phone}
            onChange={setField("phone")}
            testid="enquiry-phone"
          />
          <Field
            label="Standard / Class"
            id="enq-class"
            placeholder="e.g. Std 10, Std 12 Science"
            value={form.standard}
            onChange={setField("standard")}
            testid="enquiry-class"
          />
          <Field
            label="School Name"
            id="enq-school"
            placeholder="Current school"
            value={form.school}
            onChange={setField("school")}
            testid="enquiry-school"
          />
        </div>

        <div className="mt-2 grid grid-cols-2 gap-3">
          <Button
            onClick={handleWhatsApp}
            data-testid="enquiry-whatsapp-submit"
            className="h-11 gap-2 rounded-full bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </Button>
          <Button
            onClick={handleCall}
            data-testid="enquiry-call-submit"
            className="h-11 gap-2 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700"
          >
            <Phone className="h-4 w-4" />
            Call
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, id, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-[12px] font-semibold text-slate-700">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="h-11 rounded-md border-slate-300 focus-visible:border-red-400 focus-visible:ring-red-200"
      />
    </div>
  );
}
